import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Switch,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import {
  Warning
} from '@phosphor-icons/react';
import { useBusiness } from '../../context/BusinessContext';
import api from '../../utils/api';
import LeadCoupleAutocomplete from '../Common/LeadCoupleAutocomplete';

const EventForm = ({ event, weddingId, leadId, autoLinkWedding = false, autoLinkLead = false, onSuccess, onClose }) => {
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateAlignmentWarning, setDateAlignmentWarning] = useState(null);
  const [appointmentConflictWarning, setAppointmentConflictWarning] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);

  const [teamMembers, setTeamMembers] = useState([]);
  const [legacyAssignedOptions, setLegacyAssignedOptions] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);
  const [creatingEventType, setCreatingEventType] = useState(false);
  const [eventTypeTagMessage, setEventTypeTagMessage] = useState('');

  const businessId = selectedBusiness?.business?._id || selectedBusiness?._id || '';

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    allDay: false,
    eventType: '',
    isBlocker: true,
    maxSimultaneous: 1,
    lead: leadId || '',
    wedding: weddingId || '',
    affectsAllStaff: true,
    affectedStaff: [],
    notifyCouple: true,
    color: '#f44336',
    location: {
      type: 'on_site',
      address: '',
      videoLink: '',
      notes: ''
    },
    assignedTeamMembers: [],
    bookingGap: {
      before: 0,
      after: 0
    },
    address: '',
    notes: ''
  });

  const normaliseAssignmentMemberId = (assignmentOrId) => {
    if (!assignmentOrId) return '';
    const raw = assignmentOrId.member ?? assignmentOrId;
    if (!raw) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object' && raw._id) return String(raw._id);
    if (raw.toString) return String(raw);
    return '';
  };

  const normaliseAssignmentStaffId = (assignmentOrId) => {
    if (!assignmentOrId) return '';
    const raw = assignmentOrId.staffMember;
    if (!raw) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object' && raw._id) return String(raw._id);
    if (raw.toString) return String(raw);
    return '';
  };

  const getAssignmentToken = (assignment) => {
    const memberId = normaliseAssignmentMemberId(assignment);
    if (memberId) return `member:${memberId}`;
    const staffId = normaliseAssignmentStaffId(assignment);
    if (staffId) return `staff:${staffId}`;
    return '';
  };

  const parseAssignmentToken = (token) => {
    if (!token || typeof token !== 'string') return null;
    const [kind, id] = token.split(':');
    if (!id) return null;
    if (kind === 'member') return { member: id, role: 'primary' };
    if (kind === 'staff') return { staffMember: id, role: 'primary' };
    return null;
  };

  const normaliseAssignedTeamMembers = (assignments = []) => {
    return assignments.map((assignment) => {
      const memberId = normaliseAssignmentMemberId(assignment);
      const staffMemberId = normaliseAssignmentStaffId(assignment);
      if (!memberId && !staffMemberId) return null;
      const normalizedAssignment = { role: assignment?.role || 'primary' };
      if (memberId) normalizedAssignment.member = memberId;
      if (staffMemberId) normalizedAssignment.staffMember = staffMemberId;
      return normalizedAssignment;
    }).filter(Boolean);
  };

  const getAssignmentLabel = (assignment) => {
    const member = assignment?.member;
    if (member && typeof member === 'object') {
      const fullName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim();
      return fullName || member.staffMemberName || member.name || member.email || member.invitedEmail || 'Legacy assignment';
    }
    return 'Legacy assignment';
  };

  const toLocalDateTimeValue = (value) => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (selectedBusiness) {
      fetchTeamMembers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedBusiness, legacyAssignedOptions]);

  useEffect(() => {
    if (businessId) {
      fetchEventTypes();
    } else {
      setEventTypes([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId]);

  useEffect(() => {
    if (leadId && autoLinkLead) {
      setFormData(prev => ({ ...prev, lead: leadId }));
    } else if (weddingId && autoLinkWedding) {
      setFormData(prev => ({ ...prev, wedding: weddingId, lead: '' }));
    }
  }, [leadId, autoLinkLead, weddingId, autoLinkWedding]);

  useEffect(() => {
    if (event) {
      const startDate = event.startTime ? new Date(event.startTime) : null;
      const endDate = event.endTime ? new Date(event.endTime) : null;
      let isAllDay = false;
      if (startDate && endDate) {
        const startHour = startDate.getHours();
        const startMinute = startDate.getMinutes();
        const startSecond = startDate.getSeconds();
        const endHour = endDate.getHours();
        const endMinute = endDate.getMinutes();
        const endSecond = endDate.getSeconds();
        const isFullDay = (startHour === 0 && startMinute === 0 && startSecond === 0) &&
          ((endHour === 23 && endMinute === 59 && endSecond === 59) ||
           (endHour === 0 && endMinute === 0 && endSecond === 0 && endDate.getDate() === startDate.getDate() + 1));
        isAllDay = isFullDay || event.allDay === true;
      }
      const eventLeadId = event.lead?._id || event.lead;
      const eventWeddingId = event.wedding?._id || event.wedding;
      const normalisedAssignments = normaliseAssignedTeamMembers(event.assignedTeamMembers || []);
      const legacyOptions = (event.assignedTeamMembers || [])
        .map((assignment) => {
          const token = getAssignmentToken(assignment);
          if (!token) return null;
          return {
            token,
            label: `${getAssignmentLabel(assignment)} (legacy assignment)`,
            isLegacy: true
          };
        })
        .filter(Boolean);
      setLegacyAssignedOptions(legacyOptions);
      setFormData((prev) => ({
        ...prev,
        title: event.title || '',
        description: event.description || '',
        startTime: event.startTime ? toLocalDateTimeValue(event.startTime) : '',
        endTime: event.endTime ? toLocalDateTimeValue(event.endTime) : '',
        allDay: isAllDay,
        eventType: event.eventType || '',
        isBlocker: event.isBlocker !== undefined ? event.isBlocker : true,
        maxSimultaneous: event.maxSimultaneous || 1,
        lead: eventLeadId || leadId || '',
        wedding: eventWeddingId || weddingId || '',
        affectsAllStaff: event.affectsAllStaff !== undefined ? event.affectsAllStaff : true,
        affectedStaff: event.affectedStaff || [],
        notifyCouple: event.notifyCouple !== false,
        color: event.color || '#f44336',
        location: event.location || { type: 'on_site', address: '', videoLink: '', notes: '' },
        assignedTeamMembers: normalisedAssignments,
        bookingGap: event.bookingGap || { before: 0, after: 0 },
        address: event.address || '',
        notes: event.notes || ''
      }));
    } else {
      setLegacyAssignedOptions([]);
    }
  }, [event, weddingId, leadId]);

  // Backfill lead from wedding once leads have loaded, without resetting date/time fields.
  useEffect(() => {
    if (!event || formData.lead || !businessId) return;
    const eventWeddingId = event.wedding?._id || event.wedding;
    if (!eventWeddingId) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/api/leads', {
          params: { businessId, limit: 200, selectable: 'true' },
        });
        if (cancelled) return;
        const matchedLeadId = (response.data?.leads || []).find(
          (l) => (l.wedding?._id || l.wedding)?.toString() === eventWeddingId?.toString()
        )?._id;
        if (matchedLeadId) {
          setFormData((prev) => ({ ...prev, lead: matchedLeadId }));
        }
      } catch {
        // Non-blocking
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [event, formData.lead, businessId]);

  useEffect(() => {
    // Check date alignment only when Event Type is "Wedding" or "Wedding Day"
    const isWeddingEvent = formData.eventType === 'Wedding' || formData.eventType === 'Wedding Day';
    const weddingForCheck = formData.lead
      ? (selectedLead?.wedding?._id || selectedLead?.wedding)
      : formData.wedding;
    if (weddingForCheck && formData.startTime && isWeddingEvent) {
      checkDateAlignment(weddingForCheck);
    } else {
      setDateAlignmentWarning(null);
    }
    
    // Check for appointment conflicts when dates are set
    if (formData.startTime) {
      checkAppointmentConflicts();
    } else {
      setAppointmentConflictWarning(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [formData.lead, formData.wedding, formData.startTime, formData.endTime, formData.eventType, selectedLead]);

  const fetchTeamMembers = async () => {
    if (!selectedBusiness) return;
    
    try {
      const businessId = selectedBusiness?.business?._id || selectedBusiness?._id;
      if (!businessId) return;

      const staffResponse = await api.get(`/api/supplier/staff-members?businessId=${businessId}`);
      const staffMembers = staffResponse.data.staffMembers || [];

      const optionsById = new Map();
      staffMembers.forEach((staffMember) => {
        const linkedBusinessMemberId = staffMember.businessMember?._id || staffMember.businessMember;
        const staffMemberId = String(staffMember._id);
        const memberId = linkedBusinessMemberId ? String(linkedBusinessMemberId) : '';
        const token = memberId ? `member:${memberId}` : `staff:${staffMemberId}`;
        const staffLabel = staffMember.name
          || (`${staffMember.user?.firstName || ''} ${staffMember.user?.lastName || ''}`.trim())
          || (`${staffMember.businessMember?.user?.firstName || ''} ${staffMember.businessMember?.user?.lastName || ''}`.trim())
          || 'Unnamed';

        optionsById.set(token, {
          token,
          label: staffLabel,
          assignment: {
            ...(memberId ? { member: memberId } : {}),
            staffMember: staffMemberId,
            role: 'primary'
          },
          staffMember
        });
      });

      legacyAssignedOptions.forEach((legacyOption) => {
        if (!legacyOption?.token) return;
        if (!optionsById.has(String(legacyOption.token))) {
          optionsById.set(String(legacyOption.token), legacyOption);
        }
      });

      setTeamMembers(Array.from(optionsById.values()));
    } catch (error) {}
  };

  const fetchEventTypes = async () => {
    if (!businessId) {
      setEventTypes([]);
      return;
    }

    setLoadingEventTypes(true);
    try {
      const response = await api.get('/api/event-types', {
        params: { businessId, isActive: true }
      });
      setEventTypes(response.data.eventTypes || []);
    } catch {
      setEventTypes([]);
    } finally {
      setLoadingEventTypes(false);
    }
  };

  const checkDateAlignment = async (weddingIdToCheck) => {
    const wedding = weddingIdToCheck || formData.wedding || (formData.lead && (selectedLead?.wedding?._id || selectedLead?.wedding));
    if (!selectedBusiness || !wedding) return;
    
    try {
      const businessId = selectedBusiness?.business?._id || selectedBusiness?._id;
      if (!businessId) return;
      
      const response = await api.get(`/api/events/wedding/${wedding}/check-alignment`, {
        params: { businessId }
      });
      
      const eventDate = new Date(formData.startTime);
      const results = response.data.results || [];
      const matchingResult = results.find(r => {
        const rDate = new Date(r.eventDate);
        return rDate.toDateString() === eventDate.toDateString();
      });

      if (matchingResult && !matchingResult.alignment.aligned) {
        // Update warning message to explain it doesn't match couples planner
        const ceremonyDate = matchingResult.alignment.ceremonyDate 
          ? new Date(matchingResult.alignment.ceremonyDate).toLocaleDateString('en-GB')
          : null;
        const receptionDate = matchingResult.alignment.receptionDate 
          ? new Date(matchingResult.alignment.receptionDate).toLocaleDateString('en-GB')
          : null;
        
        let warningMessage = 'This date does not match the wedding date set in the couple\'s planner. ';
        if (ceremonyDate || receptionDate) {
          warningMessage += 'The couple has set their wedding date as ';
          if (ceremonyDate && receptionDate) {
            warningMessage += `Ceremony: ${ceremonyDate} or Reception: ${receptionDate}. `;
          } else if (ceremonyDate) {
            warningMessage += `${ceremonyDate}. `;
          } else {
            warningMessage += `${receptionDate}. `;
          }
        }
        warningMessage += 'Please reach out to discuss this with them and update the date in their planner if they have the coordinator plan.';
        setDateAlignmentWarning(warningMessage);
      } else {
        setDateAlignmentWarning(null);
      }
    } catch (error) {}
  };

  const checkAppointmentConflicts = async () => {
    if (!selectedBusiness || !formData.startTime) {
      setAppointmentConflictWarning(null);
      return;
    }

    try {
      const businessId = selectedBusiness?.business?._id || selectedBusiness?._id;
      if (!businessId) return;

      // Calculate date range for the event (use endTime if available, otherwise estimate from duration)
      const startDate = new Date(formData.startTime);
      let endDate = formData.endTime 
        ? new Date(formData.endTime)
        : new Date(startDate.getTime() + (formData.duration || 60) * 60 * 1000);

      // Expand range to check entire day to catch any appointments
      const dayStart = new Date(startDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(startDate);
      dayEnd.setHours(23, 59, 59, 999);

      // Fetch appointments for this business and date range
      const response = await api.get('/api/unified-bookings', {
        params: {
          type: 'supplier',
          startDate: dayStart.toISOString(),
          endDate: dayEnd.toISOString(),
          businessId: businessId
        }
      });

      const bookings = response.data?.bookings || [];
      
      // Filter bookings that actually overlap with the event time and are active
      const conflictingBookings = bookings.filter(booking => {
        // Only check active bookings (not cancelled or rejected)
        if (booking.status === 'cancelled' || booking.status === 'rejected') {
          return false;
        }
        if (!booking.startTime || !booking.endTime) return false;
        const bookingStart = new Date(booking.startTime);
        const bookingEnd = new Date(booking.endTime);
        
        // Check if booking overlaps with event
        return (bookingStart < endDate && bookingEnd > startDate);
      });

      // Exclude the current event if editing
      const filteredConflicts = event?._id 
        ? conflictingBookings.filter(b => b._id !== event._id)
        : conflictingBookings;

      if (filteredConflicts.length > 0) {
        const conflictCount = filteredConflicts.length;
        setAppointmentConflictWarning(
          `Warning: There ${conflictCount === 1 ? 'is' : 'are'} ${conflictCount} existing appointment${conflictCount > 1 ? 's' : ''} scheduled for this date. These appointments may need to be rescheduled.`
        );
      } else {
        setAppointmentConflictWarning(null);
      }
    } catch (error) {}
  };

  const handleInputChange = (field, value) => {
    if (field === 'eventType') {
      setEventTypeTagMessage('');
    }
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => {
        const updated = {
          ...prev,
          [field]: value
        };
        
        // Handle all-day toggle: set start/end times to start/end of day
        if (field === 'allDay' && value === true) {
          if (prev.startTime) {
            const startDate = new Date(prev.startTime);
            startDate.setHours(0, 0, 0, 0);
            updated.startTime = toLocalDateTimeValue(startDate);
          }
          if (prev.endTime) {
            const endDate = new Date(prev.endTime);
            endDate.setHours(23, 59, 59, 999);
            updated.endTime = toLocalDateTimeValue(endDate);
          } else if (prev.startTime) {
            // If no endTime, set it to end of the same day
            const endDate = new Date(prev.startTime);
            endDate.setHours(23, 59, 59, 999);
            updated.endTime = toLocalDateTimeValue(endDate);
          }
        }
        
        return updated;
      });
    }
  };

  const normalisedEventTypeName = (formData.eventType || '').trim().toLowerCase();
  const selectedEventType = eventTypes.find(
    (type) => type.name?.trim().toLowerCase() === normalisedEventTypeName
  );
  const hasCustomEventTypeName = Boolean(normalisedEventTypeName);
  const customEventTypeExists = eventTypes.some(
    (type) => type.name?.trim().toLowerCase() === normalisedEventTypeName
  );
  const canAddCurrentAsEventType = hasCustomEventTypeName && !customEventTypeExists;
  const isLegacyTag = hasCustomEventTypeName && !selectedEventType;

  const handleAddCurrentAsEventType = async () => {
    if (!businessId || !canAddCurrentAsEventType) return;
    const mappedLocationType = ['on_site', 'off_site', 'virtual'].includes(formData.location?.type)
      ? formData.location.type
      : 'on_site';
    setCreatingEventType(true);
    setEventTypeTagMessage('');
    setError('');
    try {
      const payload = {
        businessId,
        name: formData.eventType.trim(),
        color: formData.color || '#1976d2',
        duration: 60,
        isBlocker: formData.isBlocker,
        maxSimultaneous: formData.maxSimultaneous || 1,
        locationType: mappedLocationType,
        locationAddress: formData.location?.address || ''
      };
      const response = await api.post('/api/event-types', payload);
      const createdType = response.data;
      setEventTypes((prev) => {
        const withoutDuplicate = prev.filter(
          (type) => type.name?.trim().toLowerCase() !== createdType.name?.trim().toLowerCase()
        );
        return [...withoutDuplicate, createdType].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      });
      setFormData((prev) => ({
        ...prev,
        eventType: createdType.name || prev.eventType,
        color: createdType.color || prev.color
      }));
      setEventTypeTagMessage('Event type tag saved and ready to reuse.');
    } catch (saveError) {
      setError(saveError.response?.data?.message || 'Failed to save event type tag');
    } finally {
      setCreatingEventType(false);
    }
  };


  const handleSubmit = async () => {
    if (!formData.title || !formData.startTime) {
      setError('Title and start time are required');
      return;
    }

    const businessId = selectedBusiness?.business?._id || selectedBusiness?._id;
    if (!businessId) {
      setError('Business context required. Please select a business.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let startTime = new Date(formData.startTime);
      let endTime = formData.endTime ? new Date(formData.endTime) : null;
      
      // If all-day, ensure times are set to start/end of day
      if (formData.allDay) {
        startTime.setHours(0, 0, 0, 0);
        if (endTime) {
          endTime.setHours(23, 59, 59, 999);
        } else {
          // If no endTime, set to end of same day
          endTime = new Date(startTime);
          endTime.setHours(23, 59, 59, 999);
        }
      }
      
      const payload = {
        businessId,
        ...formData,
        leadId: formData.lead || undefined,
        wedding: formData.lead ? undefined : formData.wedding || undefined,
        startTime: startTime.toISOString(),
        endTime: endTime ? endTime.toISOString() : null,
        allDay: formData.allDay || false
      };

      let response;
      if (event && event._id) {
        response = await api.put(`/api/events/${event._id}`, payload);
      } else {
        response = await api.post('/api/events', payload);
      }

      if (onSuccess) {
        onSuccess(response.data.event);
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  const isEventCancelled =
    Boolean(event && (event.status === 'cancelled' || event.isCancelled));

  return (
    <>
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {event ? (isEventCancelled ? 'Edit Event (cancelled)' : 'Edit Event') : 'Create Event'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {isEventCancelled && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This event is cancelled. You can restore it to show it on the calendar again.
            </Alert>
          )}

          {dateAlignmentWarning && (
            <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
              <Typography variant="body2" component="div">
                {dateAlignmentWarning}
              </Typography>
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Event tag and colour
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use a saved tag for quick consistency, or create your own tag and colour together.
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Event tag"
                      value={formData.eventType}
                      onChange={(e) => handleInputChange('eventType', e.target.value)}
                      placeholder="e.g., Delivery, Site Visit, Team Meeting"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label="Tag colour"
                      type="color"
                      value={formData.color}
                      onChange={(e) => handleInputChange('color', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Button
                      variant="outlined"
                      onClick={handleAddCurrentAsEventType}
                      disabled={!canAddCurrentAsEventType || creatingEventType || loadingEventTypes}
                      fullWidth
                      sx={{ height: '56px' }}
                    >
                      {creatingEventType ? 'Saving tag…' : 'Save tag'}
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    {loadingEventTypes ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          Loading tags…
                        </Typography>
                      </Box>
                    ) : eventTypes.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No saved tags yet. Create one with the fields above.
                      </Typography>
                    ) : (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                          Saved tags
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {eventTypes.map((type) => {
                            const isActive = selectedEventType?._id === type._id;
                            return (
                              <Chip
                                key={type._id}
                                clickable
                                onClick={() => {
                                  setEventTypeTagMessage('');
                                  setFormData((prev) => ({
                                    ...prev,
                                    eventType: type.name || '',
                                    color: type.color || prev.color
                                  }));
                                }}
                                label={type.name}
                                variant={isActive ? 'filled' : 'outlined'}
                                icon={
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      backgroundColor: type.color || '#1976d2',
                                      border: '1px solid #ddd'
                                    }}
                                  />
                                }
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allDay}
                    onChange={(e) => handleInputChange('allDay', e.target.checked)}
                  />
                }
                label="All day event"
                componentsProps={{
                  typography: {
                    sx: { pl: 1 }
                  }
                }}
              />
              <FormHelperText>
                {formData.allDay
                  ? 'This event will show as all day in the calendar'
                  : 'Toggle to make this an all-day event'}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isBlocker}
                    onChange={(e) => handleInputChange('isBlocker', e.target.checked)}
                  />
                }
                label="Blocking Event"
                componentsProps={{
                  typography: {
                    sx: { pl: 1 }
                  }
                }}
              />
              <FormHelperText>
                {formData.isBlocker
                  ? 'This event will block all other appointments on the same day'
                  : 'Multiple events of this type can run simultaneously'}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={formData.allDay ? "Start Date" : "Start Date & Time"}
                type={formData.allDay ? "date" : "datetime-local"}
                value={formData.startTime ? (formData.allDay ? formData.startTime.slice(0, 10) : formData.startTime) : ''}
                onChange={(e) => {
                  if (formData.allDay) {
                    // For all-day, set time to start of day
                    const dateValue = e.target.value;
                    if (dateValue) {
                      const startDate = new Date(dateValue);
                      startDate.setHours(0, 0, 0, 0);
                      handleInputChange('startTime', toLocalDateTimeValue(startDate));
                      // Also update endTime to end of same day
                      const endDate = new Date(dateValue);
                      endDate.setHours(23, 59, 59, 999);
                      handleInputChange('endTime', toLocalDateTimeValue(endDate));
                    } else {
                      handleInputChange('startTime', '');
                    }
                  } else {
                    handleInputChange('startTime', e.target.value);
                  }
                }}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {!formData.allDay && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date & Time"
                  type="datetime-local"
                  value={formData.endTime}
                  onChange={(e) => handleInputChange('endTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            
            {appointmentConflictWarning && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<Warning />} sx={{ mt: 1 }}>
                  {appointmentConflictWarning}
                </Alert>
              </Grid>
            )}

            {eventTypeTagMessage && (
              <Grid item xs={12}>
                <Alert severity="success">{eventTypeTagMessage}</Alert>
              </Grid>
            )}

            {isLegacyTag && (
              <Grid item xs={12}>
                <Alert severity="info">
                  This event uses a legacy tag not in saved tags. It will keep using its current colour unless you pick or save a new tag.
                </Alert>
              </Grid>
            )}


            {!formData.isBlocker && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Simultaneous Events"
                  type="number"
                  value={formData.maxSimultaneous}
                  onChange={(e) => handleInputChange('maxSimultaneous', parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            )}

            {!autoLinkWedding && (
              <Grid item xs={12}>
                <LeadCoupleAutocomplete
                  businessId={businessId}
                  value={formData.lead || ''}
                  onChange={(leadId) => handleInputChange('lead', leadId)}
                  onLeadChange={setSelectedLead}
                  label="Link to couple (optional)"
                  allowNone
                  helperText="Link to any couple on your list. If they have a wedding, this will also show in their bookings."
                />
              </Grid>
            )}

            {formData.lead && (
              <Grid item xs={12}>
                <Box sx={{ display: 'grid', gap: 1 }}>
                  <Alert severity="info">
                    This event will be linked to the couple{selectedLead?.wedding ? '. It will also appear in their bookings.' : '.'}
                  </Alert>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notifyCouple !== false}
                        onChange={(e) => handleInputChange('notifyCouple', e.target.checked)}
                      />
                    }
                    label={formData.notifyCouple !== false
                      ? 'Notify this couple about this event'
                      : 'This is private. Do not notify this couple about this event.'}
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Location Type</InputLabel>
                <Select
                  value={formData.location.type}
                  onChange={(e) => handleInputChange('location.type', e.target.value)}
                  label="Location Type"
                >
                  <MenuItem value="on_site">On Site</MenuItem>
                  <MenuItem value="off_site">Off Site</MenuItem>
                  <MenuItem value="virtual">Virtual</MenuItem>
                  <MenuItem value="multiple">Multiple</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.location.type === 'virtual' ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Video Call Link"
                  value={formData.location.videoLink || ''}
                  onChange={(e) => handleInputChange('location.videoLink', e.target.value)}
                  placeholder="Enter video call link (e.g., Zoom, Google Meet, etc.)"
                  helperText="Provide the link for the virtual meeting"
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address || formData.location.address}
                  onChange={(e) => {
                    handleInputChange('address', e.target.value);
                    handleInputChange('location.address', e.target.value);
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assigned Team Members</InputLabel>
                <Select
                  multiple
                  value={formData.assignedTeamMembers
                    .map((tm) => getAssignmentToken(tm))
                    .filter(Boolean)}
                  onChange={(e) => {
                    const selected = e.target.value
                      .map((token) => {
                        const fromOption = teamMembers.find((option) => option.token === token)?.assignment;
                        if (fromOption) {
                          return {
                            ...(fromOption.member ? { member: fromOption.member } : {}),
                            ...(fromOption.staffMember ? { staffMember: fromOption.staffMember } : {}),
                            role: 'primary'
                          };
                        }
                        return parseAssignmentToken(token);
                      })
                      .filter(Boolean);
                    handleInputChange('assignedTeamMembers', selected);
                  }}
                  label="Assigned Team Members"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((token) => {
                        const member = teamMembers.find(m => String(m.token) === String(token));
                        const label = member
                          ? (member.label || String(token))
                          : String(token);
                        return <Chip key={String(token)} label={label} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {teamMembers.map((member) => (
                    <MenuItem key={member.token} value={member.token}>
                      {member.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onClose}>Close</Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {event && !isEventCancelled && (
            <Button
              color="error"
              variant="outlined"
              onClick={() => {
                setError('');
                setCancelConfirmOpen(true);
              }}
            >
              Cancel Event
            </Button>
          )}
          {event && isEventCancelled && (
            <Button
              color="primary"
              variant="outlined"
              onClick={() => {
                setError('');
                setRestoreConfirmOpen(true);
              }}
            >
              Restore event
            </Button>
          )}
          <Button onClick={handleSubmit} variant="contained" disabled={loading || !formData.title || !formData.startTime}>
            {loading ? <CircularProgress size={24} /> : event ? 'Update' : 'Create'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>

    <Dialog
      open={cancelConfirmOpen}
      onClose={() => !loading && setCancelConfirmOpen(false)}
      aria-labelledby="cancel-event-confirm-title"
    >
      <DialogTitle id="cancel-event-confirm-title">Cancel this event?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        <Typography>Are you sure you want to cancel this event?</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCancelConfirmOpen(false)} disabled={loading}>
          No, keep it
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={loading}
          onClick={async () => {
            if (!event?._id) return;
            setLoading(true);
            setError('');
            try {
              await api.put(`/api/events/${event._id}`, { status: 'cancelled', isCancelled: true });
              setCancelConfirmOpen(false);
              onSuccess?.();
              onClose?.();
            } catch (err) {
              setError(err.response?.data?.message || 'Failed to cancel event');
            } finally {
              setLoading(false);
            }
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Yes, cancel event'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={restoreConfirmOpen}
      onClose={() => !loading && setRestoreConfirmOpen(false)}
      aria-labelledby="restore-event-confirm-title"
    >
      <DialogTitle id="restore-event-confirm-title">Restore this event?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        <Typography>
          This will mark the event as active again and show it on the calendar.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRestoreConfirmOpen(false)} disabled={loading}>
          No
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={loading}
          onClick={async () => {
            if (!event?._id) return;
            setLoading(true);
            setError('');
            try {
              await api.put(`/api/events/${event._id}`, { status: 'active', isCancelled: false });
              setRestoreConfirmOpen(false);
              onSuccess?.();
              onClose?.();
            } catch (err) {
              setError(err.response?.data?.message || 'Failed to restore event');
            } finally {
              setLoading(false);
            }
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Yes, restore event'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EventForm;
